// authContext.tsx

import { jwtDecode } from "jwt-decode";
import React, { createContext, useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { ApiResponse } from "../apis";
import { Login } from "../apis/authenticationApis";
import { login, logout } from "../redux/slices/authSlice";
import {
  JwtPayload,
  LoginFormData,
  UserData,
} from "../shared_components/interfaces";

// Define the shape of the authentication context
interface AuthContextType {
  isAuthenticated: boolean | null;
  token: string | null;
  currentUser: any | null;
  signIn: (data: LoginFormData) => void;
  signOut: () => void;
  isValidToken: (token: string) => boolean;
}

interface AuthProviderProps {
  children?: React.ReactNode;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(false);
  const [token, setToken] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any | null>({
    userId: null,
    clientId: null,
  });
  const dispatch = useDispatch();

  // Login function
  const signIn = async (data: LoginFormData) => {
    try {
      const response: ApiResponse = await Login(data);

      const token = response.data.token;
      const decoded = jwtDecode(token) as JwtPayload;

      const tokenKey = `${process.env.REACT_APP_JWT_TOKEN_KEY}`;

      setToken(token);

      tokenKey && localStorage.setItem(tokenKey, token);
      const userData: UserData = {
        clientId: decoded.ClientId,
        userId: decoded.UserId,
        userName: decoded.UserName,
        exp: decoded.exp,
        permissions: decoded.Permissions,
        cultureId: decoded.CultureId,
        token: token,
      };

      setIsAuthenticated(true);

      setCurrentUser({
        userId: decoded.UserId,
        clientId: decoded.ClientId,
        username: decoded.UserName,
      });

      dispatch(login(userData));

      window.location.href = "/Dashboard";
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  // Logout function
  const signOut = () => {
    setIsAuthenticated(false);
    setToken(null);
    dispatch(logout());
  };

  const isValidToken = (token: string) => {
    const decodedToken: JwtPayload = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return (decodedToken.exp && decodedToken.exp > currentTime) || false;
  };

  // Provide the context value to its children
  return (
    <AuthContext.Provider
      value={{
        token,
        signIn,
        signOut,
        currentUser,
        isAuthenticated,
        isValidToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
